import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import { useCanal, type Evento } from '../lib/tiempoReal'
import type { ResumenSala } from '../lib/tipos'

/** Panel del supervisor: los números del día y lo que va entrando en vivo. */
export function Sala() {
  const clienteQuery = useQueryClient()
  const [eventos, setEventos] = useState<Array<Evento & { canal: string }>>([])

  const resumen = useQuery({
    queryKey: ['sala'],
    queryFn: () => api<ResumenSala>('/api/sala/resumen'),
  })

  useCanal('sala', (evento, canal) => {
    setEventos((previos) => [{ ...evento, canal }, ...previos].slice(0, 60))
    void clienteQuery.invalidateQueries({ queryKey: ['sala'] })
  })

  const totales = resumen.data?.totales

  const tarjetas = [
    { etiqueta: 'Llamadas abiertas', valor: totales?.llamadas },
    { etiqueta: 'Pedidos lanzados', valor: totales?.pedidos },
    { etiqueta: 'Clientes', valor: totales?.clientes },
    { etiqueta: 'CUPS', valor: totales?.cups },
    { etiqueta: 'Activados hoy', valor: totales?.activados },
    { etiqueta: 'Scoring', valor: totales?.scoring },
  ]

  return (
    <div>
      <div className="mb-1 flex flex-wrap items-baseline gap-3">
        <h1 className="text-lg font-bold">Sala</h1>
        <span className="text-sm text-slate-500">{resumen.data?.fecha}</span>
      </div>
      <p className="mb-5 text-xs text-slate-500">
        Las llamadas entrantes y los tiempos saldrán de OCM, que todavía no está conectado:
        esto cuenta lo que pasa dentro del CRM.
      </p>

      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {tarjetas.map((tarjeta) => (
          <div
            key={tarjeta.etiqueta}
            className="rounded-xl border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900"
          >
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              {tarjeta.etiqueta}
            </p>
            <p className="mt-1 text-2xl font-bold tabular-nums">{tarjeta.valor ?? '—'}</p>
          </div>
        ))}
      </div>

      <div className="mb-5 rounded-xl border border-marca-200 bg-marca-50 p-4 dark:border-marca-700/40 dark:bg-marca-700/10">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-marca-700 dark:text-marca-100">
          Puntos activados hoy
        </p>
        <p className="mt-1 text-3xl font-bold tabular-nums text-marca-800 dark:text-marca-100">
          {(resumen.data?.puntos ?? 0).toLocaleString('es-ES')}
        </p>
      </div>

      <div className="mb-5 overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-400 dark:bg-slate-800">
            <tr>
              <th className="px-4 py-2">Comercial</th>
              <th className="px-4 py-2 text-right">Llamadas</th>
              <th className="px-4 py-2 text-right">Pedidos</th>
              <th className="px-4 py-2 text-right">CUPS</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {resumen.data?.comerciales.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-center text-slate-400">
                  Hoy todavía no ha entrado nada.
                </td>
              </tr>
            )}
            {resumen.data?.comerciales.map((comercial) => (
              <tr key={comercial.uuid}>
                <td className="px-4 py-2">{comercial.nombre}</td>
                <td className="px-4 py-2 text-right tabular-nums">{comercial.llamadas}</td>
                <td className="px-4 py-2 text-right font-semibold tabular-nums">{comercial.pedidos}</td>
                <td className="px-4 py-2 text-right tabular-nums">{comercial.cups}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">En vivo</h2>
      <ul className="divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-200 bg-white dark:divide-slate-800 dark:border-slate-800 dark:bg-slate-900">
        {eventos.length === 0 && (
          <li className="px-4 py-6 text-center text-sm text-slate-400">Esperando movimiento…</li>
        )}
        {eventos.map((evento) => (
          <li key={evento.uuid} className="flex flex-wrap items-baseline gap-3 px-4 py-2 text-sm">
            <span className="font-mono text-xs text-slate-400">
              {new Date(evento.creado_en.replace(' ', 'T') + 'Z').toLocaleTimeString('es-ES')}
            </span>
            <span className="font-semibold">{evento.evento}</span>
            <span className="truncate text-slate-500">
              {Object.entries(evento.datos)
                .map(([clave, valor]) => `${clave}: ${String(valor)}`)
                .join(' · ')}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}
